import React, { Component } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';

export class AddService extends Component {
  constructor(props) {
    super(props);
    this.state = this.getInitialState(props);
  }

  getInitialState = (props) => ({
    // Service definition fields
    serviceName: props.serviceName || "",
    coverageLevel: props.coverageLevel || "",
    notes: props.notes || "",
    dataCollectRequired: props.dataCollectRequired || "Y",
    allowMultiple: props.allowMultiple || false,

    // Pricing (by vehicle size)
    retailPrices: props.retailPrices || { XL: 0, L: 0, M: 0, S: 0 },
    wholesalePrices: props.wholesalePrices || { XL: 0, L: 0, M: 0, S: 0 },
    durations: props.durations || { XL: 0, L: 0, M: 0, S: 0 },
    expectedQuantities: props.expectedQuantities || { XL: 0, L: 0, M: 0, S: 0 },

    // Use same duration for all sizes (simpler UX)
    useSingleDuration: true,
    singleDuration: props.durations?.M || props.durations?.L || 0,

    // Overlays (service hierarchy)
    // API returns master_service_id for the overridden service
    selectedOverlays: (props.currentOverlays || []).map(o => o.master_service_id),

    // Form state
    errors: {},
    activeTab: 'details'
  });

  componentDidUpdate(prevProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      // Modal just opened - reset state based on action
      if (this.props.action === "add") {
        this.setState({
          serviceName: "",
          coverageLevel: "",
          notes: "",
          dataCollectRequired: "Y",
          allowMultiple: false,
          retailPrices: { XL: 0, L: 0, M: 0, S: 0 },
          wholesalePrices: { XL: 0, L: 0, M: 0, S: 0 },
          durations: { XL: 0, L: 0, M: 0, S: 0 },
          expectedQuantities: { XL: 0, L: 0, M: 0, S: 0 },
          useSingleDuration: true,
          singleDuration: 0,
          selectedOverlays: [],
          errors: {},
          activeTab: 'details'
        });
      } else {
        // Update mode - populate with existing data
        // API returns master_service_id for the overridden service
        const currentOverlayIds = (this.props.currentOverlays || []).map(o => o.master_service_id);

        // Check if all durations are the same
        const durations = this.props.durations || { XL: 0, L: 0, M: 0, S: 0 };
        const durationsMatch = durations.L === durations.M && durations.M === durations.S && durations.S === (durations.XL || 0);

        this.setState({
          serviceName: this.props.serviceName || "",
          coverageLevel: this.props.coverageLevel || "",
          notes: this.props.notes || "",
          dataCollectRequired: this.props.dataCollectRequired || "Y",
          allowMultiple: this.props.allowMultiple || false,
          retailPrices: this.props.retailPrices || { XL: 0, L: 0, M: 0, S: 0 },
          wholesalePrices: this.props.wholesalePrices || { XL: 0, L: 0, M: 0, S: 0 },
          durations: durations,
          expectedQuantities: this.props.expectedQuantities || { XL: 0, L: 0, M: 0, S: 0 },
          useSingleDuration: durationsMatch,
          singleDuration: durations.M || durations.L || 0,
          selectedOverlays: currentOverlayIds,
          errors: {},
          activeTab: 'details'
        });
      }
    }
  }

  handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (type === 'checkbox') {
      this.setState({
        [name]: checked,
        errors: { ...this.state.errors, [name]: '' }
      });
    } else {
      const newState = {
        [name]: value,
        errors: { ...this.state.errors, [name]: '' }
      };

      // Auto-set prices and duration to 0 when service name contains "Custom"
      if (name === 'serviceName' && value.toLowerCase().includes('custom')) {
        newState.retailPrices = { XL: 0, L: 0, M: 0, S: 0 };
        newState.wholesalePrices = { XL: 0, L: 0, M: 0, S: 0 };
        newState.durations = { XL: 0, L: 0, M: 0, S: 0 };
        newState.singleDuration = 0;
      }

      this.setState(newState);
    }
  };

  handleDataCollectChange = (event) => {
    this.setState({
      dataCollectRequired: event.target.checked ? "Y" : "N"
    });
  };

  handlePriceChange = (priceType, vehicleSize, value) => {
    const priceKey = `${priceType}Prices`;
    const newPrices = {
      ...this.state[priceKey],
      [vehicleSize]: parseFloat(value) || 0
    };

    this.setState({ [priceKey]: newPrices });
  };

  handleExpectedQtyChange = (vehicleSize, value) => {
    this.setState({
      expectedQuantities: {
        ...this.state.expectedQuantities,
        [vehicleSize]: parseFloat(value) || 0
      }
    });
  };

  handleDurationChange = (vehicleSize, value) => {
    if (this.state.useSingleDuration) {
      // Apply to all sizes
      const duration = parseFloat(value) || 0;
      this.setState({
        singleDuration: duration,
        durations: { XL: duration, L: duration, M: duration, S: duration }
      });
    } else {
      // Apply to specific size
      const newDurations = {
        ...this.state.durations,
        [vehicleSize]: parseFloat(value) || 0
      };
      this.setState({ durations: newDurations });
    }
  };

  handleOverlayToggle = (serviceId) => {
    this.setState(prevState => {
      const selected = [...prevState.selectedOverlays];
      const index = selected.indexOf(serviceId);

      if (index > -1) {
        selected.splice(index, 1);
      } else {
        selected.push(serviceId);
      }

      return { selectedOverlays: selected };
    });
  };

  handleSelectAllOverlays = (filteredOverlays) => {
    const allIds = filteredOverlays.map(s => s.id);
    const allSelected = allIds.every(id => this.state.selectedOverlays.includes(id));

    if (allSelected) {
      // Deselect all
      this.setState(prevState => ({
        selectedOverlays: prevState.selectedOverlays.filter(id => !allIds.includes(id))
      }));
    } else {
      // Select all
      this.setState(prevState => ({
        selectedOverlays: [...new Set([...prevState.selectedOverlays, ...allIds])]
      }));
    }
  };

  validateForm = () => {
    const errors = {};
    const serviceName = this.state.serviceName?.trim() || "";
    const isCustomService = serviceName.toLowerCase().includes("custom");

    if (!serviceName) {
      errors.serviceName = "Service name is required";
    }

    // Validate coverage level (required for non-custom services)
    if (!isCustomService && !this.state.coverageLevel) {
      errors.coverageLevel = "Coverage level is required";
    }

    // Validate notes/description (required)
    if (!this.state.notes || this.state.notes.trim() === "") {
      errors.notes = "Description is required";
    }

    const { retailPrices, wholesalePrices } = this.state;

    if (isCustomService) {
      // Custom services MUST have all prices set to 0
      if (retailPrices.L !== 0) {
        errors.retailL = "Custom service price must be 0";
      }
      if (retailPrices.M !== 0) {
        errors.retailM = "Custom service price must be 0";
      }
      if (retailPrices.S !== 0) {
        errors.retailS = "Custom service price must be 0";
      }
      if (retailPrices.XL !== 0) {
        errors.retailXL = "Custom service price must be 0";
      }
      if (wholesalePrices.L !== 0) {
        errors.wholesaleL = "Custom service price must be 0";
      }
      if (wholesalePrices.M !== 0) {
        errors.wholesaleM = "Custom service price must be 0";
      }
      if (wholesalePrices.S !== 0) {
        errors.wholesaleS = "Custom service price must be 0";
      }
      if (wholesalePrices.XL !== 0) {
        errors.wholesaleXL = "Custom service price must be 0";
      }
    } else {
      // Non-custom services require prices > 0
      if (!retailPrices.L || retailPrices.L <= 0) {
        errors.retailL = "Large retail price is required";
      }
      if (!retailPrices.M || retailPrices.M <= 0) {
        errors.retailM = "Medium retail price is required";
      }
      if (!retailPrices.S || retailPrices.S <= 0) {
        errors.retailS = "Small retail price is required";
      }
      if (!retailPrices.XL || retailPrices.XL <= 0) {
        errors.retailXL = "X-Large retail price is required";
      }
      if (!wholesalePrices.L || wholesalePrices.L <= 0) {
        errors.wholesaleL = "Large wholesale price is required";
      }
      if (!wholesalePrices.M || wholesalePrices.M <= 0) {
        errors.wholesaleM = "Medium wholesale price is required";
      }
      if (!wholesalePrices.S || wholesalePrices.S <= 0) {
        errors.wholesaleS = "Small wholesale price is required";
      }
      if (!wholesalePrices.XL || wholesalePrices.XL <= 0) {
        errors.wholesaleXL = "X-Large wholesale price is required";
      }
    }

    // Validate duration
    if (isCustomService) {
      // Custom services MUST have duration set to 0
      if (this.state.useSingleDuration) {
        if (this.state.singleDuration !== 0) {
          errors.duration = "Custom service duration must be 0";
        }
      } else {
        if (this.state.durations.L !== 0) {
          errors.durationL = "Custom service duration must be 0";
        }
        if (this.state.durations.M !== 0) {
          errors.durationM = "Custom service duration must be 0";
        }
        if (this.state.durations.S !== 0) {
          errors.durationS = "Custom service duration must be 0";
        }
        if (this.state.durations.XL !== 0) {
          errors.durationXL = "Custom service duration must be 0";
        }
      }
    } else {
      // Non-custom services require duration > 0
      if (this.state.useSingleDuration) {
        if (!this.state.singleDuration || this.state.singleDuration <= 0) {
          errors.duration = "Duration is required";
        }
      } else {
        if (!this.state.durations.L || this.state.durations.L <= 0) {
          errors.durationL = "Large duration is required";
        }
        if (!this.state.durations.M || this.state.durations.M <= 0) {
          errors.durationM = "Medium duration is required";
        }
        if (!this.state.durations.S || this.state.durations.S <= 0) {
          errors.durationS = "Small duration is required";
        }
        if (!this.state.durations.XL || this.state.durations.XL <= 0) {
          errors.durationXL = "X-Large duration is required";
        }
      }
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = () => {
    if (!this.validateForm()) {
      return;
    }

    // Build durations object
    let durations = this.state.durations;
    if (this.state.useSingleDuration) {
      const d = this.state.singleDuration;
      durations = { XL: d, L: d, M: d, S: d };
    }

    const serviceData = {
      serviceName: this.state.serviceName.trim(),
      coverageLevel: this.state.coverageLevel || null,
      notes: this.state.notes.trim(),
      dataCollectRequired: this.state.dataCollectRequired,
      allowMultiple: this.state.allowMultiple,
      retailPrices: this.state.retailPrices,
      wholesalePrices: this.state.wholesalePrices,
      durations: durations,
      expectedQuantities: this.state.expectedQuantities,
      overlays: this.state.selectedOverlays
    };

    if (this.props.action === "add") {
      this.props.onAdd(serviceData);
    } else {
      this.props.onUpdate(serviceData);
    }
  };

  // Get coverage label for display
  getCoverageLabel = (level) => {
    const labels = {
      'FRONT': 'Front',
      'REAR': 'Rear',
      'SIDE': 'Side',
      'ROOF': 'Roof',
      'FULL': 'Full',
      'INTERIOR': 'Interior',
      'WHEEL': 'Wheel'
    };
    return labels[level] || level;
  };

  render() {
    const { show, onHide, action, categoryName, availableOverlays } = this.props;
    const modalTitle = action === "add" ? "Add Service" : "Update Service";
    const isCustomService = this.state.serviceName?.toLowerCase().includes('custom');

    // Filter available overlays based on coverage compatibility
    const filteredOverlays = (availableOverlays || []).filter(service => {
      const currentCoverage = this.state.coverageLevel;
      const candidateCoverage = service.coverage_level;

      if (!currentCoverage) return false;

      if (currentCoverage === 'FULL') {
        // FULL can overlay FRONT, REAR, SIDE, ROOF, and other FULL
        return candidateCoverage === 'FRONT' || candidateCoverage === 'REAR' ||
               candidateCoverage === 'SIDE' || candidateCoverage === 'ROOF' ||
               candidateCoverage === 'FULL';
      }
      if (currentCoverage === 'FRONT') {
        return candidateCoverage === 'FRONT';
      }
      if (currentCoverage === 'REAR') {
        return candidateCoverage === 'REAR';
      }
      if (currentCoverage === 'SIDE') {
        return candidateCoverage === 'SIDE';
      }
      if (currentCoverage === 'ROOF') {
        return candidateCoverage === 'ROOF';
      }
      if (currentCoverage === 'INTERIOR') {
        return candidateCoverage === 'INTERIOR';
      }
      if (currentCoverage === 'WHEEL') {
        return candidateCoverage === 'WHEEL';
      }
      return false;
    });

    return (
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* Service Definition Section */}
            <div className="border-bottom pb-3 mb-3">
              <h6 className="text-muted mb-3">SERVICE DETAILS</h6>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Service Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="serviceName"
                      value={this.state.serviceName}
                      onChange={this.handleInputChange}
                      isInvalid={!!this.state.errors.serviceName}
                      placeholder="e.g., Full Front End"
                    />
                    <Form.Control.Feedback type="invalid">
                      {this.state.errors.serviceName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Category</Form.Label>
                    <Form.Control
                      type="text"
                      value={categoryName || ""}
                      disabled
                      className="bg-light"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>
                      Coverage Level {!isCustomService && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Form.Control
                      as="select"
                      name="coverageLevel"
                      value={this.state.coverageLevel}
                      onChange={this.handleInputChange}
                      isInvalid={!!this.state.errors.coverageLevel}
                    >
                      <option value="">Select...</option>
                      <option value="FRONT">Front</option>
                      <option value="REAR">Rear</option>
                      <option value="SIDE">Side</option>
                      <option value="ROOF">Roof</option>
                      <option value="FULL">Full Vehicle</option>
                      <option value="INTERIOR">Interior</option>
                      <option value="WHEEL">Wheel</option>
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {this.state.errors.coverageLevel}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={9}>
                  <Form.Group>
                    <Form.Label>Description <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="notes"
                      value={this.state.notes}
                      onChange={this.handleInputChange}
                      isInvalid={!!this.state.errors.notes}
                      placeholder="e.g., Hood, fenders, front bumper, mirrors, headlights"
                    />
                    <Form.Control.Feedback type="invalid">
                      {this.state.errors.notes}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      id="dataCollectRequired"
                      label="Requires materials collection"
                      checked={this.state.dataCollectRequired === "Y"}
                      onChange={this.handleDataCollectChange}
                      className="mb-2"
                    />
                    {this.props.access === 'ADMIN' && (
                      <Form.Check
                        type="checkbox"
                        id="allowMultiple"
                        name="allowMultiple"
                        label="Allow multiple quantities"
                        checked={this.state.allowMultiple}
                        onChange={this.handleInputChange}
                      />
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Pricing Section */}
            <div className="border-bottom pb-3 mb-3">
              <h6 className="text-muted mb-3">PRICING</h6>

              {isCustomService && (
                <div className="alert alert-info py-2 mb-3">
                  <i className="fa-sharp fa-regular fa-info-circle mr-2"></i>
                  Custom services must have all prices and duration set to 0. These are entered when adding to an estimate.
                </div>
              )}

              {/* Retail Prices */}
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Label className="font-weight-bold">Retail Prices <span className="text-danger">*</span></Form.Label>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Small Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.retailPrices.S || ''}
                        onChange={(e) => this.handlePriceChange('retail', 'S', e.target.value)}
                        isInvalid={!!this.state.errors.retailS}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.retailS && (
                      <small className="text-danger">{this.state.errors.retailS}</small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Medium Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.retailPrices.M || ''}
                        onChange={(e) => this.handlePriceChange('retail', 'M', e.target.value)}
                        isInvalid={!!this.state.errors.retailM}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.retailM && (
                      <small className="text-danger">{this.state.errors.retailM}</small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Large Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.retailPrices.L || ''}
                        onChange={(e) => this.handlePriceChange('retail', 'L', e.target.value)}
                        isInvalid={!!this.state.errors.retailL}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.retailL && (
                      <small className="text-danger">{this.state.errors.retailL}</small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">X-Large Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.retailPrices.XL || ''}
                        onChange={(e) => this.handlePriceChange('retail', 'XL', e.target.value)}
                        isInvalid={!!this.state.errors.retailXL}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.retailXL && (
                      <small className="text-danger">{this.state.errors.retailXL}</small>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Wholesale Prices */}
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Label className="font-weight-bold">Wholesale Prices <span className="text-danger">*</span></Form.Label>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Small Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.wholesalePrices.S || ''}
                        onChange={(e) => this.handlePriceChange('wholesale', 'S', e.target.value)}
                        isInvalid={!!this.state.errors.wholesaleS}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.wholesaleS && (
                      <small className="text-danger">{this.state.errors.wholesaleS}</small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Medium Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.wholesalePrices.M || ''}
                        onChange={(e) => this.handlePriceChange('wholesale', 'M', e.target.value)}
                        isInvalid={!!this.state.errors.wholesaleM}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.wholesaleM && (
                      <small className="text-danger">{this.state.errors.wholesaleM}</small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">Large Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.wholesalePrices.L || ''}
                        onChange={(e) => this.handlePriceChange('wholesale', 'L', e.target.value)}
                        isInvalid={!!this.state.errors.wholesaleL}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.wholesaleL && (
                      <small className="text-danger">{this.state.errors.wholesaleL}</small>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="small">X-Large Vehicle</Form.Label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text">$</span>
                      </div>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={this.state.wholesalePrices.XL || ''}
                        onChange={(e) => this.handlePriceChange('wholesale', 'XL', e.target.value)}
                        isInvalid={!!this.state.errors.wholesaleXL}
                        placeholder="0.00"
                      />
                    </div>
                    {this.state.errors.wholesaleXL && (
                      <small className="text-danger">{this.state.errors.wholesaleXL}</small>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Duration */}
              <Row>
                <Col md={12} className="mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <Form.Label className="font-weight-bold mb-0">Duration (Days) <span className="text-danger">*</span></Form.Label>
                    <Form.Check
                      type="switch"
                      id="useSingleDuration"
                      name="useSingleDuration"
                      label="Same for all sizes"
                      checked={this.state.useSingleDuration}
                      onChange={this.handleInputChange}
                    />
                  </div>
                </Col>

                {this.state.useSingleDuration ? (
                  <Col md={4}>
                    <Form.Group>
                      <Form.Control
                        type="number"
                        step="0.25"
                        min="0.25"
                        value={this.state.singleDuration || ''}
                        onChange={(e) => this.handleDurationChange('all', e.target.value)}
                        isInvalid={!!this.state.errors.duration}
                        placeholder="0.5"
                      />
                      {this.state.errors.duration && (
                        <small className="text-danger">{this.state.errors.duration}</small>
                      )}
                    </Form.Group>
                  </Col>
                ) : (
                  <>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">Small</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.25"
                          min="0.25"
                          value={this.state.durations.S || ''}
                          onChange={(e) => this.handleDurationChange('S', e.target.value)}
                          isInvalid={!!this.state.errors.durationS}
                          placeholder="0.5"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">Medium</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.25"
                          min="0.25"
                          value={this.state.durations.M || ''}
                          onChange={(e) => this.handleDurationChange('M', e.target.value)}
                          isInvalid={!!this.state.errors.durationM}
                          placeholder="0.5"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">Large</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.25"
                          min="0.25"
                          value={this.state.durations.L || ''}
                          onChange={(e) => this.handleDurationChange('L', e.target.value)}
                          isInvalid={!!this.state.errors.durationL}
                          placeholder="0.5"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="small">X-Large</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.25"
                          min="0.25"
                          value={this.state.durations.XL || ''}
                          onChange={(e) => this.handleDurationChange('XL', e.target.value)}
                          isInvalid={!!this.state.errors.durationXL}
                          placeholder="0.5"
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>

              {/* Expected Material Usage (optional) */}
              {!isCustomService && (
                <Row className="mt-3">
                  <Col md={12} className="mb-2">
                    <Form.Label className="font-weight-bold mb-0">Expected Material Usage</Form.Label>
                    {this.props.unitLabel && (
                      <small className="text-muted ml-2">({this.props.unitLabel})</small>
                    )}
                    <small className="text-muted d-block">
                      Expected amount of material to complete this service per vehicle size. Used as default when recording materials.
                    </small>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small">Small Vehicle</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        min="0"
                        value={this.state.expectedQuantities.S || ''}
                        onChange={(e) => this.handleExpectedQtyChange('S', e.target.value)}
                        placeholder="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small">Medium Vehicle</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        min="0"
                        value={this.state.expectedQuantities.M || ''}
                        onChange={(e) => this.handleExpectedQtyChange('M', e.target.value)}
                        placeholder="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small">Large Vehicle</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        min="0"
                        value={this.state.expectedQuantities.L || ''}
                        onChange={(e) => this.handleExpectedQtyChange('L', e.target.value)}
                        placeholder="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="small">X-Large Vehicle</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        min="0"
                        value={this.state.expectedQuantities.XL || ''}
                        onChange={(e) => this.handleExpectedQtyChange('XL', e.target.value)}
                        placeholder="0"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </div>

            {/* Overlays Section - Only show if coverage level is set */}
            {this.state.coverageLevel && filteredOverlays.length > 0 && (
              <div>
                <h6 className="text-muted mb-3">SERVICE OVERLAYS</h6>
                <p className="small text-muted mb-3">
                  Select services that this service includes/overlays. When this service is added to an estimate,
                  the selected services below will be automatically removed.
                </p>

                <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {/* Select All Checkbox */}
                  <Form.Check
                    type="checkbox"
                    id="overlay-select-all"
                    className="mb-2 pb-2 border-bottom font-weight-bold"
                    label="Select All"
                    checked={filteredOverlays.length > 0 && filteredOverlays.every(s => this.state.selectedOverlays.includes(s.id))}
                    onChange={() => this.handleSelectAllOverlays(filteredOverlays)}
                  />

                  {filteredOverlays.map(service => (
                    <Form.Check
                      key={service.id}
                      type="checkbox"
                      id={`overlay-${service.id}`}
                      className="mb-2"
                      label={
                        <span>
                          {service.service_name}
                          <span className="badge badge-secondary ml-2">
                            {this.getCoverageLabel(service.coverage_level)}
                          </span>
                          {service.notes && (
                            <small className="text-muted d-block">{service.notes}</small>
                          )}
                        </span>
                      }
                      checked={this.state.selectedOverlays.includes(service.id)}
                      onChange={() => this.handleOverlayToggle(service.id)}
                    />
                  ))}
                </div>

                {this.state.selectedOverlays.length > 0 && (
                  <small className="text-info mt-2 d-block">
                    {this.state.selectedOverlays.length} service(s) will be auto-removed when this service is selected
                  </small>
                )}
              </div>
            )}

            {this.state.coverageLevel && filteredOverlays.length === 0 && (
              <div className="text-muted small">
                <i className="fa-sharp fa-regular fa-info-circle mr-2"></i>
                No other {this.getCoverageLabel(this.state.coverageLevel).toLowerCase()} coverage services available for overlay configuration.
              </div>
            )}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            CANCEL
          </Button>
          <Button variant="primary" onClick={this.handleSubmit}>
            {action === "add" ? "ADD SERVICE" : "UPDATE SERVICE"}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
